import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Send, Library } from "lucide-react";
import { QUESTION_TYPE_LABEL, type QType } from "@/lib/quiz-grading";

export const Route = createFileRoute("/teacher/question-bank")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Question Bank — MAKAI" }] }),
  component: BankPage,
});

const QTYPES: QType[] = ["mcq", "true_false", "fill_blank", "matching", "essay", "short"];
const DIFFS = ["easy", "medium", "hard"] as const;

function BankPage() {
  const ctx = useTeacherContext();
  const [items, setItems] = useState<any[]>([]);
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [filter, setFilter] = useState({ subject: "", difficulty: "", type: "", q: "" });
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState<any | null>(null);

  const load = async () => {
    if (!ctx.schoolId) return;
    let q = supabase.from("question_bank").select("*,subject:subjects_catalog(name)").eq("school_id", ctx.schoolId).order("created_at", { ascending: false });
    if (filter.subject) q = q.eq("subject_id", filter.subject);
    if (filter.difficulty) q = q.eq("difficulty", filter.difficulty as any);
    if (filter.type) q = q.eq("type", filter.type as any);
    if (filter.q) q = q.ilike("question", `%${filter.q}%`);
    const { data } = await q.limit(200);
    setItems(data ?? []);
    const { data: qs } = await supabase.from("quizzes").select("id,title").eq("school_id", ctx.schoolId).order("created_at", { ascending: false });
    setQuizzes(qs ?? []);
  };
  useEffect(() => { if (!ctx.loading) load(); /* eslint-disable-next-line */ }, [ctx.loading, ctx.schoolId, filter.subject, filter.difficulty, filter.type, filter.q]);

  async function remove(it: any) {
    if (!confirm("Delete this question from the bank?")) return;
    await supabase.from("question_bank").delete().eq("id", it.id);
    load();
  }

  return (
    <AppShell role="teacher" title="Question Bank" subtitle="Reusable library of questions across quizzes."
      action={<Button onClick={() => setCreating(true)}><Plus className="mr-1 h-4 w-4" /> New question</Button>}>
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Input placeholder="Search…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} />
        <select value={filter.subject} onChange={(e) => setFilter({ ...filter, subject: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
          <option value="">All subjects</option>
          {ctx.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
          <option value="">All types</option>
          {QTYPES.map((t) => <option key={t} value={t}>{QUESTION_TYPE_LABEL[t]}</option>)}
        </select>
        <select value={filter.difficulty} onChange={(e) => setFilter({ ...filter, difficulty: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm capitalize">
          <option value="">Any difficulty</option>
          {DIFFS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          <Library className="mx-auto mb-2 h-6 w-6" /> No questions match. Add one to start your bank.
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase text-muted-foreground">
                    {QUESTION_TYPE_LABEL[it.type as QType]} · <span className="capitalize">{it.difficulty}</span> · {it.marks}m {it.subject?.name && `· ${it.subject.name}`} {it.topic && `· ${it.topic}`}
                  </div>
                  <div className="mt-1 text-sm font-medium">{it.question}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setAdding(it)}><Send className="mr-1 h-3 w-3" /> Add to quiz</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(it)}><Edit className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-3 w-3 text-clay" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <BankEditor item={editing} schoolId={ctx.schoolId!} subjects={ctx.subjects}
          onClose={() => { setCreating(false); setEditing(null); load(); }} />
      )}
      {adding && (
        <AddToQuizDialog item={adding} quizzes={quizzes} onClose={() => setAdding(null)} />
      )}
    </AppShell>
  );
}

function BankEditor({ item, schoolId, subjects, onClose }: any) {
  const [f, setF] = useState<any>(item ?? {
    type: "mcq", difficulty: "medium", marks: 1, question: "", explanation: "", topic: "",
    subject_id: subjects[0]?.id ?? null, options: ["", "", "", ""], correct_answer: "0",
  });

  async function save() {
    if (!f.question) { toast.error("Question required"); return; }
    const { data: { user } } = await supabase.auth.getUser();
    let opts: any = null, ca: any = null;
    if (f.type === "mcq") { opts = (f.options as string[]).filter((o) => o.trim() !== ""); ca = String(f.correct_answer ?? "0"); }
    else if (f.type === "true_false") { ca = String(f.correct_answer ?? "true"); }
    else if (f.type === "fill_blank") { ca = JSON.stringify(Array.isArray(f.correct_answer) ? f.correct_answer : [String(f.correct_answer ?? "")]); }
    else if (f.type === "matching") { opts = f.options; ca = typeof f.correct_answer === "string" ? f.correct_answer : JSON.stringify(f.correct_answer ?? {}); }
    else if (f.type === "short") { ca = String(f.correct_answer ?? ""); }
    const payload = {
      school_id: schoolId, subject_id: f.subject_id || null, topic: f.topic || null,
      type: f.type, difficulty: f.difficulty, marks: Number(f.marks) || 1,
      question: f.question, options: opts, correct_answer: ca, explanation: f.explanation || null,
      created_by: user?.id,
    };
    const op = item ? supabase.from("question_bank").update(payload).eq("id", item.id) : supabase.from("question_bank").insert(payload);
    const { error } = await op;
    if (error) toast.error(error.message); else { toast.success("Saved"); onClose(); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item ? "Edit" : "New"} bank question</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-4">
            <div><Label className="text-xs">Type</Label>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, options: e.target.value === "mcq" ? ["", "", "", ""] : null, correct_answer: e.target.value === "mcq" ? "0" : e.target.value === "true_false" ? "true" : "" })}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                {QTYPES.map((t) => <option key={t} value={t}>{QUESTION_TYPE_LABEL[t]}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Difficulty</Label>
              <select value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm capitalize">
                {DIFFS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Marks</Label><Input type="number" value={f.marks} onChange={(e) => setF({ ...f, marks: e.target.value })} /></div>
            <div><Label className="text-xs">Subject</Label>
              <select value={f.subject_id ?? ""} onChange={(e) => setF({ ...f, subject_id: e.target.value })} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">—</option>
                {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <Input placeholder="Topic (optional)" value={f.topic ?? ""} onChange={(e) => setF({ ...f, topic: e.target.value })} />
          <Textarea rows={2} placeholder="Question" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
          {f.type === "mcq" && (
            <div className="space-y-2">
              {(f.options as string[]).map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="radio" name="bank-correct" checked={String(f.correct_answer) === String(i)} onChange={() => setF({ ...f, correct_answer: String(i) })} className="accent-primary" />
                  <Input value={o} onChange={(e) => { const arr = [...f.options]; arr[i] = e.target.value; setF({ ...f, options: arr }); }} placeholder={`Option ${i + 1}`} />
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setF({ ...f, options: [...f.options, ""] })}><Plus className="mr-1 h-3 w-3" /> Add option</Button>
            </div>
          )}
          {f.type === "true_false" && (
            <select value={String(f.correct_answer)} onChange={(e) => setF({ ...f, correct_answer: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="true">True</option><option value="false">False</option>
            </select>
          )}
          {f.type === "short" && <Input placeholder="Accepted answer (use | for alternatives)" value={f.correct_answer ?? ""} onChange={(e) => setF({ ...f, correct_answer: e.target.value })} />}
          {f.type === "fill_blank" && <p className="text-xs text-muted-foreground">Tip: Edit blanks after adding this question to a quiz (full editor available there).</p>}
          {f.type === "matching" && <p className="text-xs text-muted-foreground">Tip: Matching is best edited inside a quiz where you can map items visually.</p>}
          {f.type === "essay" && <p className="text-xs text-muted-foreground">Essay answers are not auto-marked.</p>}
          <Textarea rows={2} placeholder="Explanation (optional)" value={f.explanation ?? ""} onChange={(e) => setF({ ...f, explanation: e.target.value })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddToQuizDialog({ item, quizzes, onClose }: any) {
  const [quizId, setQuizId] = useState(quizzes[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  async function add() {
    if (!quizId) { toast.error("Pick a quiz"); return; }
    setBusy(true);
    const { count } = await supabase.from("quiz_questions").select("id", { count: "exact", head: true }).eq("quiz_id", quizId);
    const { error } = await supabase.from("quiz_questions").insert({
      quiz_id: quizId, type: item.type, question: item.question, options: item.options,
      correct_answer: item.correct_answer, explanation: item.explanation, marks: item.marks,
      difficulty: item.difficulty, sort_order: count ?? 0, bank_id: item.id,
    });
    setBusy(false);
    if (error) toast.error(error.message); else { toast.success("Added to quiz"); onClose(); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add to quiz</DialogTitle></DialogHeader>
        {quizzes.length === 0 ? <p className="text-sm text-muted-foreground">Create a quiz first.</p> : (
          <select value={quizId} onChange={(e) => setQuizId(e.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
            {quizzes.map((q: any) => <option key={q.id} value={q.id}>{q.title}</option>)}
          </select>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={add} disabled={busy || quizzes.length === 0}>{busy ? "Adding…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
